import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { USER_CHANNEL_SUBSCRIPTIONS } from '../../../gql/users';
import { isSubscriptionActive } from '../../../utils/channelSubscriptions.utils';
import { Button, Heading, Loader, useTheme } from 'neon-design-system';
import { Grid, useMediaQuery } from '@material-ui/core';
import { CallToActionCard } from '../../ui/CallToActionCard';
import { baseUrls } from '../../../utils/baseUrls.utils';
import { CALL_TO_ACTION } from '../../../config';
import { PremiumChannels } from './ChannelsGrid/PremiumChannels';
import { FreeChannels } from './ChannelsGrid/FreeChannels';
import ChannelPreviewList from '../../ui/ChannelPreviewList';
import { ChannelSubscription } from '../../../shared/types';
import { useHistory } from 'react-router-dom';
import ArticleList from '../../ui/ArticleList';
import { ContainerNoScroll } from '../../ui/ContainerNoScroll';

const ForYouPage: React.FC = () => {
	const history = useHistory();
	const { theme } = useTheme();
	const xsDown = useMediaQuery(theme.breakpoints.down('xs'));

	const [myArticlesCount, setMyArticlesCount] = useState<number | undefined>(undefined);
	const [articleCount, setArticleCount] = useState<number | undefined>(undefined);

	const queryChannelSubscriptions = useQuery(USER_CHANNEL_SUBSCRIPTIONS, { fetchPolicy: 'network-only' });

	const activeChannelSubscriptions: ChannelSubscription[] = useMemo(() => {
		if (!queryChannelSubscriptions.data) return [];

		return queryChannelSubscriptions
			.data
			.me
			.channelSubscriptions
			.filter(isSubscriptionActive);
	}, [queryChannelSubscriptions.data]);

	if (queryChannelSubscriptions.loading) return <Loader />;

	const haveSubscriptions = activeChannelSubscriptions.length > 0;

	return (
		<ContainerNoScroll>
			<Grid container spacing={8}>
				<Grid item xs={12}>
					<Heading styleName="largeTitle">All the latest market insights, tailored for you.</Heading>
				</Grid>
				{haveSubscriptions && <>
					{(myArticlesCount === undefined || myArticlesCount > 0) && <Grid item xs={12}>
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<Heading xs="small" md="medium" xl="large" styleName="title1">From your subscriptions</Heading>
							</Grid>
							<Grid item xs={12}>
								<ArticleList source="forYou" showPagination={false} onlyMySubscriptions onDataLoaded={t => setMyArticlesCount(t)}/>
							</Grid>
						</Grid>
					</Grid>}
					<Grid item xs={12}>
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<Grid container spacing={2} justifyContent="space-between">
									<Grid item xs={12} sm={6}>
										<Heading xs="small" md="medium" xl="large" styleName="title1">Your channel subscriptions</Heading>
									</Grid>
									<Grid item xs={12} sm={6} style={{ textAlign: xsDown ? 'left' : 'right' }}>
										<Button colorType="primary" size="medium" onClick={() => history.push(baseUrls.subscriptions)}>Manage subscriptions</Button>
									</Grid>
								</Grid>
							</Grid>
							<Grid item xs={12}>
								<ChannelPreviewList channels={activeChannelSubscriptions.map(cs => cs.channel)} loading={false} />
							</Grid>
						</Grid>
					</Grid>
				</>}
				{(articleCount === undefined || articleCount > 0) && <Grid item xs={12}>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<Heading xs="small" md="medium" xl="large" styleName="title1">Latest free articles</Heading>
						</Grid>
						<Grid item xs={12}>
							<ArticleList source="forYou" showPagination={false} freeOnly excludeMySubscriptions onDataLoaded={t => setArticleCount(t)} />
						</Grid>
					</Grid>
				</Grid>}
				{!haveSubscriptions && <Grid item xs={12}>
					<CallToActionCard
						title="Stay ahead of the curve"
						subtitle="Channels is a dedicated space for our expert contributors to share their insights and market commentary, whilst ensuring that you can access their content from one central platform."
						buttonText="Browse channels"
						buttonLink={baseUrls.channels.main}
						imageUrl={CALL_TO_ACTION}
					/>
				</Grid>}
				<Grid item xs={12}>
					<PremiumChannels
						randomize
						quantityToDisplay={3}
					/>
				</Grid>
				<Grid item xs={12}>
					<FreeChannels
						randomize
						quantityToDisplay={3}
					/>
				</Grid>
			</Grid>
		</ContainerNoScroll>
	);
}

export default ForYouPage;
